import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import styled from 'styled-components';
import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Fade,
  Grid,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
  ZoomIn as ZoomInIcon
} from '@material-ui/icons';

import { useElementSize } from '../../../common/elementSize.hook';
import { ErrorContent } from '../../../common/views/ErrorView';
import { createProxyUrl } from '../Document.hook';

const StyledContainer = styled(Grid)`
  height: 100%;
  position: relative;
  background-color: rgb(68, 68, 68);
  .wrapper {
    position: absolute;
    width: 100%;
    height: 100%;
    display: block;
    overflow: auto;
    align-items: center;
  }
  .text {
    color: #fff;
  }
  .text-secondary {
    color: rgba(255, 255, 255, 0.7);
  }
  .document {
    height: 100%;
  }
  .page {
    padding-top: 5px;
    &:not(:last-child) {
      padding-bottom: 5px;
    }
    &:last-child {
      padding-bottom: 68px;
    }

    /* hide page annotations */
    .pdf-page .react-pdf__Page__annotations {
      display: none;
    }
  }
  canvas {
    margin-left: auto;
    margin-right: auto;
    box-shadow: ${props => props.theme.shadows[8]};
  }
  .zoom {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    display: flex;
    margin-bottom: 16px;
    color: rgb(237, 239, 245);
    .MuiButtonGroup-root {
      margin-left: auto;
      margin-right: auto;
      background-color: rgba(0, 0, 0, 0.6);
    }
  }
`;

interface PageData {
  visible: boolean;
  // preserve original height
  height?: number;
  // height multiplier, for scaling
  multiplier: number;
}

export interface PDFViewerProps {
  url?: string;
  storageKey?: string;
  className?: string;
  resizeOnce?: boolean;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  url,
  storageKey,
  className,
  resizeOnce = false
}) => {
  const scaleAdjust: number = 0.1;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver>();
  const [error, setError] = useState<Error | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pdf, setPdf] = useState<pdfjs.PDFDocumentProxy | undefined>();
  const [scale, setScale] = useState<number>(1);
  const [width, setWidth] = useState<number | undefined>();
  const [pages, setPagesState] = useState<PageData[]>([]);
  const pagesRef = useRef(pages);

  const setPages: typeof setPagesState = value => {
    if (typeof value === 'function') {
      setPagesState(pages => {
        const updated = value(pages);
        pagesRef.current = updated;
        return updated;
      });
    } else {
      pagesRef.current = value;
      setPagesState(value);
    }
  };

  const [containerWidthOnce] = useElementSize({
    once: true,
    elementRef: containerRef
  });
  const [containerWidth] = useElementSize({
    once: resizeOnce,
    elementRef: containerRef
  });

  const fileUrl: string | undefined = useMemo(() => {
    return storageKey
      ? createProxyUrl(storageKey, undefined, true).toString()
      : url;
  }, [url, storageKey]);

  useEffect(() => {
    setWidth(containerWidthOnce);
  }, [containerWidthOnce]);

  const createObserver = () => {
    return new IntersectionObserver(
      entries => {
        const intersects: { pageIndex: number; intersects: boolean }[] = [];

        for (const entry of entries) {
          const element = entry.target as HTMLDivElement;
          const attr = element.getAttribute('data-page-index');
          if (!attr) {
            continue;
          }

          const pageIndex = parseInt(attr);
          if (!isFinite(pageIndex)) {
            continue;
          }

          const page = pagesRef.current[pageIndex];
          if (entry.isIntersecting && page && !page.visible) {
            intersects.push({ pageIndex, intersects: true });
          }
        }

        if (intersects.length > 0) {
          const copy = pagesRef.current.slice();

          for (const item of intersects) {
            copy[item.pageIndex] = {
              ...copy[item.pageIndex],
              visible: item.intersects
            };
          }

          setPages(copy);
        }
      },
      { root: containerRef.current, rootMargin: '600px 0px', threshold: 0 }
    );
  };

  return (
    <StyledContainer
      container
      direction='column'
      className={className}
      innerRef={(ref: HTMLDivElement | null) => (containerRef.current = ref)}
    >
      <div
        className='wrapper'
        style={pdf ? undefined : { justifyContent: 'center' }}
      >
        <Document
          file={fileUrl}
          error=''
          loading=''
          className='document'
          onLoadSuccess={pdf => {
            // destroy previous observer if any
            observerRef.current?.disconnect();
            observerRef.current = createObserver();

            const pages: PageData[] = [];
            for (let index = 0; index < pdf.numPages; index++) {
              // make first 2 pages visible
              pages.push({ visible: index < 2, multiplier: 1 });
            }

            setPdf(pdf);
            setIsLoading(false);
            setPages(pages);
          }}
          onLoadError={(error: Error) => {
            setIsLoading(false);
            setError(error);
          }}
        >
          {pages.map((page, index) => (
            <PdfPage
              key={`page-${index}`}
              page={page}
              pageIndex={index}
              scale={scale}
              width={width || 0}
              innerRef={ref => ref && observerRef.current?.observe(ref)}
              onChange={page => {
                const copy = pagesRef.current.slice();
                copy[index] = { ...copy[index], ...page };
                setPages(copy);
              }}
            />
          ))}
        </Document>

        {isLoading && !error && (
          <Box display='flex' alignItems='center' flexDirection='column'>
            <CircularProgress color='primary' />
            <Box mt={2}>
              <Typography variant='body1' className='text'>
                Loading PDF...
              </Typography>
            </Box>
          </Box>
        )}
        {!isLoading && error && (
          <ErrorContent
            title='PDF Error'
            className='text'
            description={
              <Typography variant='body1' className='text-secondary'>
                {error.message || 'Failed to load PDF file.'}
              </Typography>
            }
            icon={
              <WarningIcon
                fontSize='inherit'
                htmlColor='rgba(255, 255, 255, 0.5)'
              />
            }
          />
        )}
      </div>

      <Fade in={!!pdf}>
        <div className='zoom'>
          <ButtonGroup color='inherit' variant='text'>
            <Tooltip arrow title='Zoom Out'>
              <Button
                onClick={() => {
                  const newScale: number = scale - scaleAdjust;
                  setScale(newScale <= scaleAdjust ? scaleAdjust : newScale);
                }}
              >
                <RemoveIcon />
              </Button>
            </Tooltip>
            <Tooltip arrow title='Fit to width'>
              <Button
                onClick={() => {
                  setScale(1);
                  setWidth(containerWidth);
                }}
              >
                <ZoomInIcon />
              </Button>
            </Tooltip>
            <Tooltip arrow title='Zoom In'>
              <Button onClick={() => setScale(scale + scaleAdjust)}>
                <AddIcon color='inherit' />
              </Button>
            </Tooltip>
          </ButtonGroup>
        </div>
      </Fade>
    </StyledContainer>
  );
};

export default PDFViewer;

interface PdfPageProps {
  page: PageData;
  pageIndex: number;
  width: number;
  scale: number;
  innerRef?: (ref: HTMLDivElement | null) => void;
  onChange?: (page: Partial<PageData>) => void;
}

const PdfPage: React.FC<PdfPageProps> = props => {
  const { page, pageIndex, scale, innerRef, onChange } = props;
  const [didRender, setDidRender] = useState(false);
  const width = props.width - 64;
  const height = page.height
    ? page.height * page.multiplier * scale + 10
    : '100%';
  return (
    <div
      ref={innerRef}
      className='page'
      data-page-index={pageIndex}
      style={{ height: !page.visible ? height : undefined }}
    >
      {(didRender || page.visible) && (
        <Page
          width={width}
          scale={scale}
          pageNumber={pageIndex + 1}
          className='pdf-page'
          loading={
            <Box
              padding={2}
              display='flex'
              alignItems='center'
              flexDirection='column'
            >
              <CircularProgress color='primary' />
              <Box mt={2}>
                <Typography variant='body1' className='text'>
                  Loading Page {pageIndex + 1}...
                </Typography>
              </Box>
            </Box>
          }
          onLoadSuccess={page => {
            // once the page is rendered, avoid conditionally removing it and use
            // css styling to hide it instead to avoid re-rendering the PDF page
            setDidRender(true);
            // save height for placeholder when page is not within viewport
            onChange?.({
              height: page.originalHeight,
              multiplier: page.height / (page.originalHeight * scale)
            });
          }}
        />
      )}
    </div>
  );
};
